import React, { useEffect, useState } from 'react';
import { Send, Loader2, Search, Users, UserCheck, BellRing } from 'lucide-react';
import { api } from '../../context/AuthContext';
import { toast } from 'sonner';

const MODI = [
  { k: 'alle', label: 'Alle Portal-Nutzer', icon: Users, hint: 'Jeder Mandant mit Portal-Zugang erhält die Aufforderung.' },
  { k: 'auswahl', label: 'Einzelne Mandanten', icon: UserCheck, hint: 'Mandanten per Suche ankreuzen.' },
  { k: 'offen', label: 'Nur noch nicht bestätigt', icon: BellRing, hint: 'Erinnerung an alle, die die gewählten Texte noch nicht bestätigt haben.' },
];
const DOCS = [{ k: 'agb', label: 'AGB' }, { k: 'datenschutz', label: 'Datenschutz' }];
const fehlerText = (e) => e?.response?.data?.detail || 'Versand fehlgeschlagen.';

const Auswahl = ({ gewaehlt, setGewaehlt, tick }) => {
  const [q, setQ] = useState('');
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    setBusy(true);
    const t = setTimeout(() => api.get('/admin/rechtstexte/nutzer', { params: { q } }).then((r) => setItems(r.data.items)).catch(() => setItems([])).finally(() => setBusy(false)), 250);
    return () => clearTimeout(t);
  }, [q, tick]);
  const toggle = (u) => setGewaehlt((g) => (g[u.user_id] ? Object.fromEntries(Object.entries(g).filter(([k]) => k !== u.user_id)) : { ...g, [u.user_id]: u }));
  return (
    <div className="border border-gray-200 mt-3" data-testid="rt-senden-auswahl">
      <label className="relative block border-b border-gray-100"><Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-[#7a8a99]" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name, E-Mail oder Mandantennummer" data-testid="rt-senden-suche" className="w-full pl-8 pr-3 py-2 text-xs focus:outline-none" />
      </label>
      <ul className="max-h-60 overflow-y-auto divide-y divide-gray-50">
        {busy && <li className="px-3 py-3 text-xs text-[#7a8a99] flex items-center gap-2"><Loader2 className="w-3 h-3 animate-spin" /> Suche …</li>}
        {!busy && items.length === 0 && <li className="px-3 py-4 text-xs text-center text-[#7a8a99]" data-testid="rt-senden-keine">Keine Portal-Nutzer gefunden.</li>}
        {!busy && items.map((u) => (
          <li key={u.user_id}><label className="flex flex-wrap items-center gap-2 px-3 py-1.5 text-xs cursor-pointer hover:bg-[#f5f8fa]" data-testid={`rt-senden-nutzer-${u.user_id}`}>
            <input type="checkbox" checked={!!gewaehlt[u.user_id]} onChange={() => toggle(u)} className="accent-[#b8893d]" data-testid={`rt-senden-check-${u.user_id}`} />
            <span className="text-[#1B3A5C]">{u.name || '—'}</span><span className="text-[#7a8a99]">{u.email}</span>
            {u.mandanten_nrs.length > 0 && <span className="font-mono text-[#34536b] bg-[#f5f8fa] px-1.5">M-{u.mandanten_nrs.join(', M-')}</span>}
            <span className={`ml-auto ${u.fehlt.length ? 'text-rose-700' : 'text-emerald-700'}`}>{u.fehlt.length ? `offen: ${u.fehlt.map((f) => (f === 'agb' ? 'AGB' : 'Datenschutz')).join(', ')}` : 'bestätigt'}</span>
          </label></li>
        ))}
      </ul>
    </div>
  );
};

export const RechtstexteSenden = ({ onDone }) => {
  const [modus, setModus] = useState('offen');
  const [docs, setDocs] = useState(['agb', 'datenschutz']);
  const [gewaehlt, setGewaehlt] = useState({});
  const [busy, setBusy] = useState(false);
  const [bestaetigen, setBestaetigen] = useState(null);
  const [tick, setTick] = useState(0);
  const anzahl = Object.keys(gewaehlt).length;
  useEffect(() => { setBestaetigen(null); }, [modus, docs, gewaehlt]);
  const body = () => ({ modus, docs, user_ids: Object.keys(gewaehlt) });
  const pruefen = async () => {
    setBusy(true);
    try { const r = await api.post('/admin/rechtstexte/senden', { ...body(), vorschau: true }); setBestaetigen(r.data.anzahl); } catch (e) { toast.error(fehlerText(e)); }
    setBusy(false);
  };
  const senden = async () => {
    setBusy(true);
    try {
      const r = await api.post('/admin/rechtstexte/senden', body());
      toast.success(`Zustimmung angefordert bei ${r.data.anzahl} Empfänger(n)${r.data.mail === 'nicht konfiguriert' ? ' · E-Mail-Versand nicht konfiguriert' : ' · E-Mails werden versendet'}`);
      setGewaehlt({}); setBestaetigen(null); setTick((x) => x + 1); onDone();
    } catch (e) { toast.error(fehlerText(e)); }
    setBusy(false);
  };
  return (
    <div className="bg-white border border-gray-200 p-4" data-testid="rt-senden">
      <p className="text-[11px] tracking-[0.2em] uppercase text-[#1B3A5C] font-semibold">Zur Zustimmung senden</p>
      <p className="text-xs text-[#7a8a99] mt-1">Neue Fassungen gehen weiterhin automatisch an alle. Hier können Sie gezielt nachsenden – die Empfänger müssen die Texte beim nächsten Login bestätigen.</p>
      <div className="flex flex-wrap gap-2 mt-3">
        {DOCS.map((d) => (
          <label key={d.k} className="inline-flex items-center gap-1.5 text-xs text-[#1B3A5C] border border-gray-200 px-2.5 py-1 cursor-pointer" data-testid={`rt-senden-doc-${d.k}`}>
            <input type="checkbox" checked={docs.includes(d.k)} onChange={() => setDocs((x) => (x.includes(d.k) ? x.filter((y) => y !== d.k) : [...x, d.k]))} className="accent-[#b8893d]" />{d.label}
          </label>
        ))}
      </div>
      <div className="grid sm:grid-cols-3 gap-2 mt-3">
        {MODI.map(({ k, label, icon: Icon, hint }) => (
          <button key={k} type="button" onClick={() => setModus(k)} data-testid={`rt-senden-modus-${k}`}
            className={`text-left border px-3 py-2 transition-colors ${modus === k ? 'border-[#b8893d] bg-[#b8893d]/5' : 'border-gray-200 hover:border-[#b8893d]/50'}`}>
            <span className="flex items-center gap-1.5 text-xs text-[#1B3A5C] font-medium"><Icon className="w-3.5 h-3.5 text-[#b8893d]" />{label}</span>
            <span className="block text-[11px] text-[#7a8a99] mt-0.5">{hint}</span>
          </button>
        ))}
      </div>
      {modus === 'auswahl' && <Auswahl gewaehlt={gewaehlt} setGewaehlt={setGewaehlt} tick={tick} />}
      <div className="flex items-center gap-3 mt-3">
        {modus === 'auswahl' && <span className="text-xs text-[#34536b]" data-testid="rt-senden-anzahl">{anzahl} ausgewählt</span>}
        {bestaetigen !== null ? (
          <span className="ml-auto flex flex-wrap items-center gap-2" data-testid="rt-senden-bestaetigen">
            <span className="text-xs text-[#1B3A5C]">Wirklich an <b>{bestaetigen}</b> Empfänger senden?</span>
            <button type="button" onClick={() => setBestaetigen(null)} data-testid="rt-senden-abbrechen" className="px-3 py-2 text-[11px] tracking-wider border border-gray-300 text-[#1B3A5C] hover:border-[#1B3A5C] transition-colors">ABBRECHEN</button>
            <button type="button" onClick={senden} disabled={busy} data-testid="rt-senden-ja" className="inline-flex items-center gap-1.5 px-4 py-2 text-[11px] tracking-wider bg-[#b8893d] text-white hover:bg-[#1B3A5C] transition-colors disabled:opacity-40">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} JETZT SENDEN
            </button>
          </span>
        ) : (
        <button type="button" onClick={pruefen} disabled={busy || !docs.length || (modus === 'auswahl' && !anzahl)} data-testid="rt-senden-button"
          className="ml-auto inline-flex items-center gap-1.5 px-4 py-2 text-[11px] tracking-wider bg-[#1B3A5C] text-white hover:bg-[#b8893d] transition-colors disabled:opacity-40">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />} {modus === 'offen' ? 'ERINNERUNG SENDEN' : 'ZUR ZUSTIMMUNG SENDEN'}
        </button>
        )}
      </div>
    </div>
  );
};

export const VersandLog = ({ items }) => (
  <div className="bg-white border border-gray-200" data-testid="rt-versand-log">
    <p className="px-4 py-3 border-b border-gray-100 text-[11px] tracking-[0.2em] uppercase text-[#1B3A5C] font-semibold">Versandprotokoll</p>
    <ul className="divide-y divide-gray-50">
      {items.length === 0 && <li className="px-4 py-4 text-xs text-center text-[#7a8a99]">Noch nichts gezielt versendet.</li>}
      {items.map((v, i) => (
        <li key={i} className="px-4 py-2 text-xs flex flex-wrap gap-3" data-testid={`rt-versand-${i}`}>
          <span className="text-[#7a8a99]">{new Date(v.am).toLocaleString('de-DE', { dateStyle: 'short', timeStyle: 'short' })}</span>
          <span className="text-[#1B3A5C]">{v.modus === 'erstzugang' ? 'Erinnerung Erstzugang' : MODI.find((m) => m.k === v.modus)?.label}</span>
          <span className="text-[#34536b]">{v.docs.map((d) => (d === 'agb' ? 'AGB' : 'Datenschutz')).join(' + ')}</span>
          <span className="ml-auto text-[#7a8a99]">{v.anzahl} Empfänger · {v.mail === 'gesendet' ? `${v.gesendet} E-Mails` : v.mail === 'wird gesendet' ? 'E-Mails werden gesendet' : v.mail === 'unterbrochen' ? 'Versand unterbrochen' : 'ohne E-Mail'}{v.fehler?.length ? ` · ${v.fehler.length} fehlgeschlagen` : ''} · von {v.von}</span>
          {v.fehler?.length > 0 && <span className="w-full text-rose-700" data-testid={`rt-versand-fehler-${i}`}>Nicht zugestellt: {v.fehler.join(', ')}</span>}
        </li>
      ))}
    </ul>
  </div>
);
